import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, AlertTriangle, Loader2, Send, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import MapView from '@/components/MapView';
import { getLoadStatusClasses, getDocumentStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function AdminDriverDetail() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [driver, setDriver] = useState(null);
  const [loads, setLoads] = useState([]);
  const [compliance, setCompliance] = useState({ docs: {}, gps: [], hos: null });
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [earningsStart, setEarningsStart] = useState('');
  const [earningsEnd, setEarningsEnd] = useState('');

  const fetchNotes = () => {
    if (!token || !driverId) return;
    api.get(`/clients/${driverId}/notes`)
      .then(r => setNotes(r.data))
      .catch(() => setNotes([]));
  };

  useEffect(() => {
    if (!token || !driverId) return;
        Promise.all([
      api.get('/drivers'),
      api.get('/loads'),
      api.get('/admin/drivers-compliance').catch(() => ({ data: [] })),
      api.get('/tracking/compliance').catch(() => ({ data: [] })),
    ]).then(([driversRes, loadsRes, compRes, gpsRes]) => {
      const allDrivers = driversRes.data || [];
      const drv = allDrivers.find(d => d.user_id === driverId);
      if (drv) setDriver(drv);
      setLoads(loadsRes.data || []);
      const compData = compRes.data || [];
      const drvComp = compData.find(d => d.user_id === driverId);
      const gpsEvents = (gpsRes.data || []).filter(g => g.driver_id === driverId);
      setCompliance({ docs: drvComp?.documents || {}, gps: gpsEvents, hos: drvComp });
      // Extract documents from compliance data
      const drvDocs = drvComp?.document_list || drvComp?.docs_list || [];
      setDocuments(Array.isArray(drvDocs) ? drvDocs : []);
    }).catch(console.error).finally(() => setLoading(false));
    fetchNotes();
    api.get(`/loads?driver_id=${driverId}`).then(r => {
      const driverLoads = r.data || [];
      const events = driverLoads.map(l => ({
        date: l.created_at, status: l.status,
        desc: `Load ${l.load_id?.slice(-6)} - ${l.cargo_description || 'Load'}`,
        detail: `${l.pickup_name || ''} → ${l.delivery_name || ''} | $${l.rate || 0}`,
      }));
      events.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivity(events.slice(0, 50));
    }).catch(() => {});
  }, [token, driverId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/clients/${driverId}/notes`, { content: newNote.trim() });
      setNewNote(''); fetchNotes(); toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!driver) return <div className="text-center py-12"><p className="text-muted-foreground">Driver not found</p><Button variant="outline" onClick={() => navigate('/admin/drivers')} className="mt-4 border-input text-muted-foreground">Back to Drivers</Button></div>;

  const driverLoads = loads.filter(l => l.driver_id === driverId);
  const active = driverLoads.filter(l => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status));
  const allCompleted = driverLoads.filter(l => ['completed', 'invoiced'].includes(l.status));

  // Date-filtered completed loads for earnings/km
  const completed = allCompleted.filter(l => {
    if (!earningsStart && !earningsEnd) return true;
    const d = l.completed_at || l.delivered_at || l.updated_at || '';
    if (earningsStart && d < earningsStart) return false;
    if (earningsEnd && d > earningsEnd + 'T23:59:59') return false;
    return true;
  });

  const totalKm = completed.reduce((s, l) => s + milesToKm(l.distance_miles), 0);
  const totalEarnings = completed.reduce((s, l) => s + (l.rate || 0), 0);

  const mapMarkers = [];
  const mapLoads = [];
  if (driver.last_location?.lat) {
    mapMarkers.push({ id: 'drv', lat: driver.last_location.lat, lng: driver.last_location.lng, label: driver.name, type: 'truck', color: '#E5A100' });
  }
  active.forEach(ld => {
    if (ld.pickup_lat) mapMarkers.push({ id: `${ld.load_id}-p`, lat: ld.pickup_lat, lng: ld.pickup_lng, label: ld.pickup_name || 'Pickup', type: 'pickup' });
    if (ld.delivery_lat) mapMarkers.push({ id: `${ld.load_id}-d`, lat: ld.delivery_lat, lng: ld.delivery_lng, label: ld.delivery_name || 'Delivery', type: 'delivery' });
    mapLoads.push({ ...ld, driver_lat: driver.last_location?.lat, driver_lng: driver.last_location?.lng });
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/drivers')} className="text-muted-foreground hover:text-foreground p-2 h-auto">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-16 h-16 rounded-full bg-brand-accent/20 flex items-center justify-center text-2xl font-bold text-brand-accent">{driver.name?.charAt(0)}</div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{driver.name}</h1>
            <p className="text-sm text-muted-foreground">{driver.email}{driver.phone ? ` · ${driver.phone}` : ''}</p>
          </div>
          <Badge className={`ml-4 text-xs ${active.length > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
            {active.length > 0 ? 'ON ROAD' : 'IDLE'}
          </Badge>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <p className="text-[10px] text-muted-foreground tracking-wider font-bold">EARNINGS PERIOD</p>
        <Input type="date" value={earningsStart} onChange={e => setEarningsStart(e.target.value)} className="h-8 w-40 bg-surface border-input text-xs text-muted-foreground" placeholder="Start" />
        <span className="text-muted-foreground text-xs">to</span>
        <Input type="date" value={earningsEnd} onChange={e => setEarningsEnd(e.target.value)} className="h-8 w-40 bg-surface border-input text-xs text-muted-foreground" placeholder="End" />
        {(earningsStart || earningsEnd) && (
          <Button variant="ghost" size="sm" onClick={() => { setEarningsStart(''); setEarningsEnd(''); }} className="text-muted-foreground hover:text-foreground text-xs h-8">Clear</Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Active Loads', value: active.length, accent: 'text-foreground' },
          { label: 'Completed', value: completed.length, accent: 'text-emerald-400' },
          { label: 'Total KM', value: totalKm.toLocaleString(), accent: 'text-brand-accent' },
          { label: 'Earnings', value: `$${totalEarnings.toLocaleString()}`, accent: 'text-brand-accent' },
          { label: 'Compliance', value: compliance.hos?.compliance_score != null ? `${compliance.hos.compliance_score}%` : 'N/A', accent: 'text-purple-400' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4">
            <p className={`text-xl font-bold ${s.accent}`}>{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* 3 Column Layout: Map (wide) | Load History */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Column 1: Map + Active Loads (spans 2 cols) */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">LIVE MAP</p>
              <div className="h-[380px] rounded-xl overflow-hidden border border-border">
                <MapView markers={mapMarkers} loads={mapLoads} dark={false} zoom={6} showSatelliteToggle showLegend />
              </div>
            </CardContent>
          </Card>

          {/* Active Loads below map */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ACTIVE LOADS ({active.length})</p>
              {active.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No active loads</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {active.map(ld => (
                  <div key={ld.load_id} onClick={() => navigate(`/admin/loads/${ld.load_id}`)} className="bg-muted/50 rounded-lg p-3 cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-foreground font-medium truncate">{ld.cargo_description}</p>
                      <Badge className={`text-[10px] ${getLoadStatusClasses(ld.status)}`}>{ld.status?.replace(/_/g, ' ').toUpperCase()}</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{(ld.pickup_name || ld.pickup_address)?.split(',')[0]} → {(ld.delivery_name || ld.delivery_address)?.split(',')[0]}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{milesToKm(ld.distance_miles)} km | ${ld.rate?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Column 2: Load History */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">LOAD HISTORY ({completed.length})</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <p className="text-sm font-bold text-brand-accent">${totalEarnings.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">EARNED</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <p className="text-sm font-bold text-foreground">{totalKm.toLocaleString()} km</p>
                <p className="text-[10px] text-muted-foreground">DRIVEN</p>
              </div>
            </div>
            <div className="max-h-[500px] overflow-y-auto space-y-2">
              {completed.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No completed loads</p>}
              {completed.map(ld => (
                <div key={ld.load_id} className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-foreground font-medium truncate">{ld.cargo_description}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{(ld.pickup_name || ld.pickup_address)?.split(',')[0]} → {(ld.delivery_name || ld.delivery_address)?.split(',')[0]}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-muted-foreground">{milesToKm(ld.distance_miles)} km</span>
                    <span className="text-[10px] text-brand-accent font-mono">${ld.rate?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Below Grid: Details + Compliance + Notes + Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Driver Details + Compliance */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-4">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">DETAILS</p>
            {driver.truck_type && <Row label="Truck Type" value={driver.truck_type} />}
            {driver.license_number && <Row label="CDL License" value={driver.license_number} />}
            <Row label="Status" value={driver.onboarding_completed ? 'Active' : 'Onboarding'} />
            <Row label="Profile Completion" value={`${driver.profile_completion_pct || 0}%`} />

            {driver.emergency_contact_name && (
              <div className="border-t border-border pt-3">
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">EMERGENCY CONTACT</p>
                <p className="text-sm text-foreground">{driver.emergency_contact_name}</p>
                {driver.emergency_contact_phone && <p className="text-xs text-muted-foreground">{driver.emergency_contact_phone}</p>}
                {driver.emergency_contact_relation && <p className="text-xs text-muted-foreground">{driver.emergency_contact_relation}</p>}
              </div>
            )}

            <div className="border-t border-border pt-3">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">COMPLIANCE</p>
              <Row label="Score" value={compliance.hos?.compliance_score != null ? `${compliance.hos.compliance_score}%` : 'N/A'} />
              <Row label="Documents" value={`${compliance.docs.approved || 0} approved, ${compliance.docs.expired || 0} expired`} />
              <Row label="GPS Events" value={compliance.gps.length} />
            </div>

            {compliance.gps.length > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold">RECENT GPS EVENTS</p>
                {compliance.gps.slice(0, 5).map((g, i) => (
                  <div key={i} className="bg-muted/50 rounded-lg p-2 flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <span className="text-[10px] text-muted-foreground">{g.event_type?.replace(/_/g, ' ')}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{g.gap_duration_seconds ? `${Math.round(g.gap_duration_seconds / 60)}m` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Log */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ACTIVITY LOG</p>
            <div className="space-y-0 max-h-[300px] overflow-auto">
              {activity.length === 0 ? <p className="text-muted-foreground text-xs py-2">No activity yet</p> :
                activity.map((ev, i) => (
                  <div key={i} className="flex gap-2 pb-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        ev.status === 'completed' ? 'bg-emerald-400' :
                        ev.status === 'in_transit' ? 'bg-blue-400' :
                        ev.status === 'assigned' ? 'bg-purple-400' : 'bg-muted-foreground'
                      }`} />
                      {i < activity.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                    </div>
                    <div>
                      <p className="text-xs text-foreground">{ev.desc}</p>
                      <p className="text-[10px] text-muted-foreground">{ev.detail}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(ev.date).toLocaleString()}</p>
                    </div>
                  </div>
                ))
              }
            </div>
          </CardContent>
        </Card>

        {/* Internal Notes */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">INTERNAL NOTES</p>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {notes.length === 0 ? <p className="text-muted-foreground text-xs py-2">No notes yet</p> :
                notes.map(n => (
                  <div key={n.id || n.created_at} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-brand-accent font-medium">{n.author_name || 'Admin'}</span>
                      <span className="text-[10px] text-muted-foreground">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{n.content}</p>
                  </div>
                ))
              }
            </div>
            <div className="flex gap-2 pt-2 border-t border-border">
              <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="h-9 bg-surface border-input text-xs text-muted-foreground" onKeyDown={e => e.key === 'Enter' && handleAddNote()} />
              <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()} className="bg-brand-accent hover:bg-brand-accent/80 h-9"><Send className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Documents */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ONBOARDING DOCUMENTS</p>
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {documents.length === 0 ? <p className="text-muted-foreground text-xs py-2">No documents found</p> :
                documents.map((doc, i) => (
                  <div key={doc.id || i} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-foreground font-medium truncate">{doc.name || doc.document_type || doc.type || 'Document'}</p>
                      <Badge className={`text-[10px] ${getDocumentStatusClasses(doc.status)}`}>
                        {(doc.status || 'pending').toUpperCase()}
                      </Badge>
                    </div>
                    {doc.type && <p className="text-[10px] text-muted-foreground">{doc.type}</p>}
                    {doc.expiry_date && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-xs text-foreground">{value}</span>
    </div>
  );
}
